import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '@clerk/react'
import {
  fetchDueFacts,
  confirmDueFact,
  renewDueFact,
  certDateToEpoch,
  epochToCertDate,
  describeOverdue,
  describeQueue,
  RENEW_EXPLANATION,
  type DueFact,
} from '@/lib/api'

/**
 * THE RE-CONFIRMATION QUEUE (Build 3a) — what has gone out of date.
 *
 * RENDERS NOTHING WHEN NOTHING IS DUE, and nothing while loading or on error.
 * This is the one surface on the dashboard that is USUALLY empty: facts fall due
 * on a sixty-day-and-up cadence, so a permanent "Nothing needs confirming" panel
 * would be furniture the owner learns to scroll past — and then miss on the week
 * it finally has something in it. An empty queue is best communicated by an
 * absence. (A load failure is also silent for the same reason it is silent in
 * the counters card: this is the least urgent thing on the screen, and a red
 * banner over an empty queue would be noise about nothing.)
 *
 * ABOVE THE TABS, deliberately: this is work the assistant has already decided
 * needs him, unlike the tabs below which are places to go looking.
 *
 * ONE CARD, TWO PRODUCTS (Build 10e). Moved here whole from
 * src/routes/Dashboard.tsx, not copied (§20.15), because Kelvo's weekly text
 * says "N things need confirming" and its owner had nowhere to confirm them:
 * Kelvo's /report now renders this same card. Foredesk renders it with no
 * props and reads exactly what it always did; `audience` changes only who the
 * sentence says is being told.
 */
export default function NeedsConfirmingCard({ audience = 'customers' }: { audience?: 'customers' | 'staff' }) {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const [facts, setFacts] = useState<DueFact[] | null>(null)

  const load = useCallback(async () => {
    try {
      const token = await getTokenRef.current()
      setFacts(await fetchDueFacts(token))
    } catch {
      setFacts([])
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  if (facts === null || facts.length === 0) return null

  return (
    <section className="mb-6 rounded-card border border-border-subtle bg-surface-raised px-4 py-4">
      <h2 className="font-display text-sm font-semibold tracking-tight text-text-primary">
        {describeQueue(facts.length)}
      </h2>
      <p className="mt-1 text-xs text-text-muted">
        Your assistant is still telling {audience} these. Confirm the ones that are still right, and
        fix the ones that aren't.
      </p>

      <ul className="mt-4 stack-y-3">
        {facts.map((f) => (
          <li key={f.id}>
            <DueFactRow fact={f} onDone={load} />
          </li>
        ))}
      </ul>
    </section>
  )
}

function DueFactRow({ fact, onDone }: { fact: DueFact; onDone: () => void }) {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  // Pre-filled with the expiry already on file: the owner is usually reading off
  // a new certificate that starts where the old one ended, and an empty field
  // makes him find the format himself.
  const [certDate, setCertDate] = useState(() => epochToCertDate(fact.expiresAt))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function run(action: () => Promise<void>) {
    setBusy(true)
    setError(null)
    try {
      await action()
      onDone()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not save. Try again.')
    } finally {
      // Latent rather than visible: this row leaves its list on success, so the
      // stuck state unmounts before anyone reads it. Luck, not correctness — the
      // pattern is the one that bit the publish button, so it is removed rather
      // than left waiting for a list that keeps its rows.
      setBusy(false)
    }
  }

  function confirm() {
    void run(async () => {
      await confirmDueFact(await getTokenRef.current(), fact.id)
    })
  }

  function renew() {
    // Converted HERE by the shared helper, which returns the END of the chosen
    // day: a certificate that runs "to 15 March" is valid ON the 15th, and
    // recording midnight would have the assistant hedging live cover a day early.
    const seconds = certDateToEpoch(certDate)
    if (seconds === null) {
      setError('That is not a date the certificate could have.')
      return
    }
    if (seconds <= Math.floor(Date.now() / 1000)) {
      // Refused here as well as by the engine, so the owner gets a reason rather
      // than a rejection. A lapsed certificate is a different job from a renewal.
      setError('That date has already passed. A lapsed certificate needs replacing, not renewing.')
      return
    }
    void run(async () => {
      await renewDueFact(await getTokenRef.current(), fact.id, seconds)
    })
  }

  return (
    <div className="rounded-card border border-border-subtle px-3 py-3">
      <p className="text-sm text-text-primary">{fact.content}</p>
      <p className="mt-1 text-xs text-text-muted">{describeOverdue(fact.daysOverdue)}</p>

      {fact.action === 'confirm' ? (
        <div className="mt-3">
          <button
            onClick={confirm}
            disabled={busy}
            aria-busy={busy}
            className="rounded-lg bg-brand-primary px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
          >
            {busy ? 'Saving…' : 'Still right'}
          </button>
        </div>
      ) : (
        <div className="mt-3">
          <p className="text-xs text-text-muted">{RENEW_EXPLANATION}</p>
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <div>
              <label
                htmlFor={`cert-${fact.id}`}
                className="block text-xs font-medium text-text-muted"
              >
                New expiry date
              </label>
              <input
                id={`cert-${fact.id}`}
                type="date"
                value={certDate}
                onChange={(e) => setCertDate(e.target.value)}
                className="mt-1 rounded-lg border border-border-subtle bg-surface-raised px-3 py-2 text-sm text-text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 disabled:opacity-50"
              />
            </div>
            <button
              onClick={renew}
              disabled={busy || certDate === ''}
              aria-busy={busy}
              className="rounded-lg bg-brand-primary px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
            >
              {busy ? 'Saving…' : 'Save date'}
            </button>
          </div>
        </div>
      )}

      {error ? (
        <p role="alert" className="mt-2 text-xs text-text-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
}
